<template>
    <div v-if="state" class="ais-GeoSearch">
        <GmapMap
            ref="map"
            :center="center"
            :zoom="zoom"
            class="ais-GeoSearch-map"
            :clickable="true"
            :options="options"
            @zoom_changed="zoom = $event"
            @bounds_changed="boundsChanged"
        >
            <GmapCluster
                :zoom-on-click="true"
                :max-zoom="13"
                :grid-size="100"
                :styles="[
                    {
                        textColor: 'white',
                        textSize: '14',
                        fontWeight: '700',
                        fontFamily: 'Gotham, Helvetica, sans-serif',
                        url: '/dist/images/clusters/size1.png',
                        height: 54,
                        width: 54
                    },
                    {
                        textColor: 'white',
                        textSize: '14',
                        fontWeight: '700',
                        fontFamily: 'Gotham, Helvetica, sans-serif',
                        url: '/dist/images/clusters/size2.png',
                        height: 62,
                        width: 62
                    },
                    {
                        textColor: 'white',
                        textSize: '14',
                        fontWeight: '700',
                        fontFamily: 'Gotham, Helvetica, sans-serif',
                        url: '/dist/images/clusters/size3.png',
                        height: 78,
                        width: 78
                    },
                    {
                        textColor: 'white',
                        textSize: '14',
                        fontWeight: '700',
                        fontFamily: 'Gotham, Helvetica, sans-serif',
                        url: '/dist/images/clusters/size4.png',
                        height: 94,
                        width: 94
                    }
                ]"
            >
                <GmapMarker
                    v-for="item in $root.hits"
                    :key="item.id"
                    :position="item._geoloc"
                    :clickable="true"
                    :icon="getMarkerIcon(item)"
                    @click="$emit('set-active-marker', item)"
                />
            </GmapCluster>

            <GmapInfoWindow
                v-if="activeMarker && !$root.isMobile"
                :options="infoWindowOptions"
                :position="activeMarker._geoloc"
            />
        </GmapMap>

        <template v-if="activeMarker && $root.isMobile">
            <MapViewInstaller
                v-if="$root.index == 'installers'"
                class="ais-GeoSearch-mobilepopup"
                :item="activeMarker"
                @opened="scrollIntoView"
            />
            <MapViewRetailer
                v-else
                class="ais-GeoSearch-mobilepopup"
                :item="activeMarker"
                @opened="scrollIntoView"
            />
        </template>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import { createWidgetMixin } from 'vue-instantsearch';
import { connectGeoSearch } from 'instantsearch.js/es/connectors';
import MapViewInstaller from './MapViewInstaller';
import MapViewRetailer from './MapViewRetailer';

export default {
    components: {
        MapViewInstaller,
        MapViewRetailer
    },

    mixins: [createWidgetMixin({ connector: connectGeoSearch })],

    props: {
        activeMarker: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            zoom: 5,
            center: { lat: -25.274398, lng: 133.775136 },

            zoomOnSearch: 12,
            zoomOnSelect: 13,
            showSidebarZoom: 13,

            marker: {
                inactive: '/dist/images/pin-grey.png',
                active: '/dist/images/pin-orange.png',
                secondary: '/dist/images/pin-green.png'
            },

            options: {
                maxZoom: 15,
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                disableDefaultUI: false,
                styles: [
                    {
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#f5f5f5'
                            }
                        ]
                    },
                    {
                        elementType: 'labels.icon',
                        stylers: [
                            {
                                visibility: 'off'
                            }
                        ]
                    },
                    {
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#616161'
                            }
                        ]
                    },
                    {
                        elementType: 'labels.text.stroke',
                        stylers: [
                            {
                                color: '#f5f5f5'
                            }
                        ]
                    },
                    {
                        featureType: 'administrative.land_parcel',
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#bdbdbd'
                            }
                        ]
                    },
                    {
                        featureType: 'poi',
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#eeeeee'
                            }
                        ]
                    },
                    {
                        featureType: 'poi',
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#757575'
                            }
                        ]
                    },
                    {
                        featureType: 'poi.park',
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#e5e5e5'
                            }
                        ]
                    },
                    {
                        featureType: 'poi.park',
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#9e9e9e'
                            }
                        ]
                    },
                    {
                        featureType: 'road',
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#ffffff'
                            }
                        ]
                    },
                    {
                        featureType: 'road.arterial',
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#757575'
                            }
                        ]
                    },
                    {
                        featureType: 'road.highway',
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#dadada'
                            }
                        ]
                    },
                    {
                        featureType: 'road.highway',
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#616161'
                            }
                        ]
                    },
                    {
                        featureType: 'road.local',
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#9e9e9e'
                            }
                        ]
                    },
                    {
                        featureType: 'transit.line',
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#e5e5e5'
                            }
                        ]
                    },
                    {
                        featureType: 'transit.station',
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#eeeeee'
                            }
                        ]
                    },
                    {
                        featureType: 'water',
                        elementType: 'geometry',
                        stylers: [
                            {
                                color: '#c9c9c9'
                            }
                        ]
                    },
                    {
                        featureType: 'water',
                        elementType: 'geometry.fill',
                        stylers: [
                            {
                                color: '#4bbeea'
                            }
                        ]
                    },
                    {
                        featureType: 'water',
                        elementType: 'labels.text.fill',
                        stylers: [
                            {
                                color: '#9e9e9e'
                            }
                        ]
                    }
                ]
            }
        };
    },

    computed: {
        infoWindowOptions() {
            const installer = `
                <div class="map-popup">
                    <p>${this.activeMarker.firstName} ${this.activeMarker.lastName}</p>
                </div>`;

            const retailer = `
                <div class="map-popup">
                    <div class="map-popup-image">
                        <img src="${this.activeMarker.logo}" alt="${this.activeMarker.title}" />
                    </div>
                    <p>${this.activeMarker.title}</p>
                </div>`;

            const content = this.$root.index == 'installers' ? installer : retailer;

            return {
                content,
                //optional: offset infowindow so it visually sits nicely on top of our marker
                pixelOffset: {
                    width: 0,
                    height: -35
                }
            };
        }
    },

    watch: {
        activeMarker(newVal) {
            if (!newVal) return;

            if (this.$root.isMobile) this.scrollIntoView();

            if (this.zoom < this.zoomOnSelect) {
                this.zoom = this.zoomOnSelect;
                this.center = newVal._geoloc;
            }
        }
    },

    created() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                // Temp set to VIC
                // this.center = { lat: -37.8143, lng: 144.963 };
                this.center = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                this.zoom = 10;
                this.$emit('map-moved', { zoom: this.zoom, synced: !this.showMapSearchButton });
            });
        }
    },

    methods: {
        scrollIntoView() {
            // Scroll map to top
            this.$nextTick(() => {
                this.$refs.map.$el.scrollIntoView({ block: 'end', scrollBehavior: 'smooth' });
            });
        },

        boundsChanged() {
            // If zoom equal or more than resultThreshold refine using bounds
            if (this.zoom >= this.showSidebarZoom) {
                this.searchBounds();
            }
            // else {
            //     const coords = {
            //         northEast: { lat: -10.6681857235, lng: 153.569469029 },
            //         southWest: { lat: -43.6345972634, lng: 113.338953078 }
            //     };

            //     this.state.refine(coords);
            // }

            this.$emit('active-sidebar', this.zoom >= this.showSidebarZoom);
        },

        searchBounds: debounce(function() {
            const bounds = this.$refs.map.$mapObject.getBounds();

            this.state.refine({
                northEast: {
                    lat: bounds.getNorthEast().lat(),
                    lng: bounds.getNorthEast().lng()
                },
                southWest: {
                    lat: bounds.getSouthWest().lat(),
                    lng: bounds.getSouthWest().lng()
                }
            });
        }, 500),

        getMarkerIcon(marker) {
            if (this.activeMarker && this.activeMarker.id === marker.id) return this.marker.active;
            if (this.activeMarker && this.activeMarker.id !== marker.id)
                return this.marker.inactive;
            if (marker.isFounding) return this.marker.secondary;
            return this.marker.active;
        },

        // Set center of map to latlng received from places autocomplete and trigger refinement
        setBounds(latlng) {
            this.center = latlng;
            this.zoom = this.zoomOnSearch;
        }
    }
};
</script>

<style lang="scss" scoped>
.ais-GeoSearch {
    position: relative;
}

.ais-GeoSearch-map {
    height: 100%;
}

.ais-GeoSearch-mobilepopup {
    position: absolute;
    left: 1em;
    right: 1em;
    bottom: 0;
}

.search-map-button {
    position: absolute;
    top: 1em;
    left: 50%;
    transform: translateX(-50%);

    @media screen and (min-width: 769px) {
        top: auto;
        bottom: 1em;
    }
}

::v-deep {
    .map-popup {
        margin: 0.5em;
        padding: 1em;
        background-color: #ffffff;
        border-radius: 5px;
        font-size: 15px;
        font-weight: 500;
    }

    .map-popup-image {
        width: 75px;
        height: 40px;
        margin-bottom: 1em;

        img {
            height: 100%;
            object-fit: contain;
            object-position: center;
        }
    }
}
</style>
